/* eslint-disable @typescript-eslint/no-use-before-define */
import {
    Action,
    ActionReducerMap,
    createFeatureSelector,
    createSelector,
    StoreModule,
} from '@ngrx/store';
import { MapPolyline } from '@polaris-customerweb/sharedcomponents.shared';
import {
    PlannedRide,
    PlannedRideViapoint,
    PlannedRideWaypoint,
} from '@polaris-customerweb/sharedcomponents.user-content';
import { MapPoint } from 'src/app/models/map-point';
import { UuidService } from 'src/app/services/uuid.service';
import {
    AddMapPointToPlannedRide,
    AddMapPolylineToPlannedRide,
    AddPlannedRide,
    ForcePlannedRideRefresh,
    PlannedRideAction,
    RemoveMapPointFromPlannedRide,
    RemovePlannedRide,
    SetActivePlannedRide,
    SetAdhocWaypoint,
    SetEditablePlannedRide,
    SetExcludeRouteOptions,
    SetIsRidePlanning,
    SetIsRideTypeSelected,
    SetPlannedRides,
    SetTempViapoint,
    ToggleActivePlannedRide,
    UpdatePlannedRide,
} from './planned-ride.actions';

export interface PlannedRideState {
    activePlannedRide?: PlannedRide;
    addMapPoint?: MapPoint;
    addMapPolyline?: MapPolyline;
    adhocWaypoint?: PlannedRideWaypoint;
    editablePlannedRide: {
        current?: PlannedRide;
        history: PlannedRide[];
        future: PlannedRide[];
    };
    excludeRouteOptions: string;
    forceRefresh: boolean;
    isRidePlanning: boolean;
    isRideTypeSelected: boolean;
    plannedRides: PlannedRide[];
    removeMapPoint?: MapPoint;
    tempViapoint?: PlannedRideViapoint;
}

const reducerMap: ActionReducerMap<PlannedRideState> = {
    activePlannedRide: activePlannedRideReducer,
    addMapPoint: addMapPointReducer,
    addMapPolyline: addMapPolylineReducer,
    adhocWaypoint: adhocWaypointReducer,
    editablePlannedRide: editablePlannedRideReducer,
    excludeRouteOptions: excludeRouteOptionsReducer,
    forceRefresh: forceRefreshReducer,
    isRidePlanning: isRidePlanningReducer,
    isRideTypeSelected: isRideTypeSelectedReducer,
    plannedRides: plannedRidesReducer,
    removeMapPoint: removeMapPointReducer,
    tempViapoint: tempViapointReducer,
};

export const selectPlannedRideState =
    createFeatureSelector<PlannedRideState>('plannedRide');
export const selectActivePlannedRide = createSelector(
    selectPlannedRideState,
    (state) => state.activePlannedRide
);
export const selectAddMapPointToPlannedRide = createSelector(
    selectPlannedRideState,
    (state) => state.addMapPoint
);
export const selectAddMapPolylineToPlannedRide = createSelector(
    selectPlannedRideState,
    (state) => state.addMapPolyline
);
export const selectAdhocWaypoint = createSelector(
    selectPlannedRideState,
    (state) => state.adhocWaypoint
);
export const selectEditablePlannedRide = createSelector(
    selectPlannedRideState,
    (state) => state.editablePlannedRide.current
);

export const selectEditablePlannedRideCanUndo = createSelector(
    selectPlannedRideState,
    (state) => state.editablePlannedRide.history.length > 1
);

export const selectEditablePlannedRideCanRedo = createSelector(
    selectPlannedRideState,
    (state) => state.editablePlannedRide.future.length > 0
);
export const selectExcludeRouteOptions = createSelector(
    selectPlannedRideState,
    (state) => state.excludeRouteOptions
);
export const selectIsRidePlanning = createSelector(
    selectPlannedRideState,
    (state) => state.isRidePlanning
);
export const selectIsRideTypeSelected = createSelector(
    selectPlannedRideState,
    (state) => state.isRideTypeSelected
);
export const selectPlannedRides = createSelector(
    selectPlannedRideState,
    (state) => state.plannedRides
);
export const selectRemoveMapPointFromPlannedRide = createSelector(
    selectPlannedRideState,
    (state) => state.removeMapPoint
);
export const selectTempViapoint = createSelector(
    selectPlannedRideState,
    (state) => state.tempViapoint
);
export const selectShouldForcePlannedRideRefresh = createSelector(
    selectPlannedRideState,
    (state) => state.forceRefresh
);

export const plannedRideStoreModule = StoreModule.forFeature(
    'plannedRide',
    reducerMap
);

function activePlannedRideReducer(state: PlannedRide, action: Action) {
    switch (action.type) {
        case PlannedRideAction.setActivePlannedRide:
            return (action as SetActivePlannedRide).plannedRide;
        case PlannedRideAction.toggleActivePlannedRide:
            if (
                (action as ToggleActivePlannedRide).plannedRide.id === state.id
            ) {
                return undefined;
            }

            return (action as ToggleActivePlannedRide).plannedRide;
        // TODO: investigate this solution
        // case CollectionAction.setActiveCollection:
        // case DealerAction.setActiveDealer:
        // case GeofenceAction.setActiveGeofence:
        // case PlaceAction.setActivePointOfInterest:
        // case PlaceAction.setActivePremiumPlace:
        // case PlannedRideAction.setActivePlannedRide:
        // case RideConditionAction.setActiveRideCondition:
        // case RideEventAction.setActiveRideEvent:
        // case RideGroupActions.setActiveRideGroup:
        // case RideGroupActions.setActiveRideGroupRider:
        // case TrackedRideAction.setActiveTrackedRide:
        // case WaypointAction.setActivePublicWaypoint:
        // case WaypointAction.setActiveWaypoint:
        //     return undefined;
        default:
            return state;
    }
}

function addMapPointReducer(state: MapPoint, action: Action) {
    switch (action.type) {
        case PlannedRideAction.addMapPointToPlannedRide:
            return (action as AddMapPointToPlannedRide).mapPoint;
        default:
            return state;
    }
}

function addMapPolylineReducer(state: MapPolyline, action: Action) {
    switch (action.type) {
        case PlannedRideAction.addMapPolylineToPlannedRide:
            return (action as AddMapPolylineToPlannedRide).mapPolyline;
        default:
            return state;
    }
}

function adhocWaypointReducer(state: PlannedRideWaypoint, action: Action) {
    switch (action.type) {
        case PlannedRideAction.setAdhocWaypoint:
            return (action as SetAdhocWaypoint).adhocWaypoint;
        default:
            return state;
    }
}

function editablePlannedRideReducer(
    state: {
        current: PlannedRide | undefined;
        history: PlannedRide[];
        future: PlannedRide[];
    } = { current: undefined, future: [], history: [] },
    action: Action
) {
    let current: PlannedRide | undefined;
    let history: PlannedRide[];
    let future: PlannedRide[];
    switch (action.type) {
        case PlannedRideAction.setEditablePlannedRide:
            current = (action as SetEditablePlannedRide).plannedRide?.clone();
            if (!(action as SetEditablePlannedRide).includeInHistory) {
                history = [...state.history];
                future = [...state.future];
                return { current, future, history };
            }
            history = (action as SetEditablePlannedRide).plannedRide
                ? [...state.history, ...(current ? [current.clone()] : [])]
                : [];
            future = [] as PlannedRide[];
            return { current, future, history };
        case PlannedRideAction.undoEditablePlannedRide:
            current = state.history[state.history.length - 2].clone();
            history = [...state.history.slice(0, -1)];
            future = [...state.future, state.history[state.history.length - 1]];
            return { current, future, history };
        case PlannedRideAction.redoEditablePlannedRide:
            current = state.future[state.future.length - 1].clone();
            history = [...state.history, current.clone()];
            future = [...state.future.slice(0, -1)];
            return { current, future, history };
        default:
            return state;
    }
}

function excludeRouteOptionsReducer(state: string, action: Action) {
    switch (action.type) {
        case PlannedRideAction.setExcludeRouteOptions:
            return (action as SetExcludeRouteOptions).excludeRouteOptions;
        default:
            return state;
    }
}

function forceRefreshReducer(state = true, action: Action) {
    switch (action.type) {
        case PlannedRideAction.forcePlannedRideRefresh:
            return (action as ForcePlannedRideRefresh).forceRefresh;
        default:
            return state;
    }
}

function isRidePlanningReducer(state = false, action: Action) {
    switch (action.type) {
        case PlannedRideAction.setIsRidePlanning:
            return (action as SetIsRidePlanning).isRidePlanning;
        default:
            return state;
    }
}

function isRideTypeSelectedReducer(state = false, action: Action) {
    switch (action.type) {
        case PlannedRideAction.setIsRideTypeSelected:
            return (action as SetIsRideTypeSelected).isRideTypeSelected;
        default:
            return state;
    }
}

function plannedRidesReducer(state: PlannedRide[] = [], action: Action) {
    switch (action.type) {
        case PlannedRideAction.addPlannedRide:
            return [...state, (action as AddPlannedRide).plannedRide.clone()];
        case PlannedRideAction.removePlannedRide:
            return state.filter(
                (plannedRide) =>
                    plannedRide.id !==
                    (action as RemovePlannedRide).plannedRideId
            );
        case PlannedRideAction.setPlannedRides:
            return [
                ...(action as SetPlannedRides).plannedRides,
                ...state.filter(
                    (plannedRide) => plannedRide.id === UuidService.empty
                ),
            ];
        case PlannedRideAction.updatePlannedRide:
            return [
                ...state.filter(
                    (plannedRide) =>
                        plannedRide.id !==
                        (action as UpdatePlannedRide).plannedRide.id
                ),
                (action as UpdatePlannedRide).plannedRide.clone(),
            ];
        default:
            return state;
    }
}

function removeMapPointReducer(state: MapPoint, action: Action) {
    switch (action.type) {
        case PlannedRideAction.removeMapPointFromPlannedRide:
            return (action as RemoveMapPointFromPlannedRide).mapPoint;
        default:
            return state;
    }
}

function tempViapointReducer(state: PlannedRideViapoint, action: Action) {
    switch (action.type) {
        case PlannedRideAction.setTempViapoint:
            return (action as SetTempViapoint).tempViapoint;
        default:
            return state;
    }
}
